import { initializeCtaTracking } from "@/shared/analytics/ctaTracking";
import { captureAttribution } from "@/shared/analytics/attribution";

const REVEAL_THRESHOLD = 0.15;
const MAX_REVEAL_DELAY = 0.15;
const REVEAL_DELAY_STEP = 0.03;
const MOBILE_BREAKPOINT = "(max-width: 767px)";
const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";
const STICKY_HIDDEN_TRANSLATE_Y = "translateY(1rem)";

function initializeRevealAnimations() {
  if (!("IntersectionObserver" in window) || window.matchMedia(REDUCED_MOTION).matches) return;

  document.documentElement.classList.add("js-reveal");

  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      entry.target.classList.add("visible");
      revealObserver.unobserve(entry.target);
    });
  }, { threshold: REVEAL_THRESHOLD, rootMargin: "0px 0px 150px 0px" });

  document.querySelectorAll(".reveal").forEach((element, index) => {
    element.style.transitionDelay = `${Math.min(index * REVEAL_DELAY_STEP, MAX_REVEAL_DELAY)}s`;
    revealObserver.observe(element);
  });
}

function initializeMobileStickyCta() {
  const heroPrimaryCtaButtonDesktop = document.querySelector("#hero-primary-cta-button-desktop");
  const mobileStickyCta = document.querySelector("#mobile-sticky-cta");

  if (!mobileStickyCta) return;

  const mobileMediaQuery = window.matchMedia(MOBILE_BREAKPOINT);
  let stickyTriggerY = 0;

  const setStickyVisibility = (shouldShow) => {
    mobileStickyCta.style.display = shouldShow ? "block" : "none";
    mobileStickyCta.style.opacity = shouldShow ? "1" : "0";
    mobileStickyCta.style.transform = shouldShow ? "translateY(0)" : STICKY_HIDDEN_TRANSLATE_Y;
    mobileStickyCta.classList.toggle("visible", shouldShow);
  };

  const computeStickyTriggerY = () => {
    if (!heroPrimaryCtaButtonDesktop) {
      stickyTriggerY = Number.POSITIVE_INFINITY;
      return;
    }

    const buttonRect = heroPrimaryCtaButtonDesktop.getBoundingClientRect();
    stickyTriggerY = buttonRect.top + window.scrollY + heroPrimaryCtaButtonDesktop.offsetHeight;
  };

  const syncStickyCta = () => {
    const shouldShow = mobileMediaQuery.matches && window.scrollY > stickyTriggerY;
    setStickyVisibility(shouldShow);
  };

  const recomputeAndSyncStickyCta = () => {
    computeStickyTriggerY();
    syncStickyCta();
  };

  recomputeAndSyncStickyCta();

  window.addEventListener("load", recomputeAndSyncStickyCta);
  window.addEventListener("scroll", syncStickyCta, { passive: true });
  window.addEventListener("resize", recomputeAndSyncStickyCta);
  mobileMediaQuery.addEventListener("change", recomputeAndSyncStickyCta);
}

function initializeReviewCarousel() {
  const carousel = document.querySelector("[data-review-carousel]");

  if (!carousel) return;

  const track = carousel.querySelector("[data-review-carousel-track]");
  const dotList = carousel.querySelector("[data-review-carousel-dots]");
  const arrows = Array.from(carousel.querySelectorAll("[data-review-carousel-arrow]"));
  const cards = Array.from(track.children);

  if (cards.length < 2) return;

  const reducedMotion = window.matchMedia(REDUCED_MOTION);
  const cardStride = () => cards[1].offsetLeft - cards[0].offsetLeft;
  const visibleCards = () => Math.max(1, Math.round(track.clientWidth / cardStride()));
  const pageCount = () => Math.ceil(cards.length / visibleCards());
  const pageStride = () => visibleCards() * cardStride();
  const currentPage = () => Math.min(pageCount() - 1, Math.round(track.scrollLeft / pageStride()));
  const stepOf = (arrow) => (arrow.dataset.reviewCarouselArrow === "next" ? 1 : -1);
  const withinRange = (page) => page >= 0 && page < pageCount();

  let dots = [];

  const goToPage = (page) => {
    track.scrollTo({
      left: page * pageStride(),
      behavior: reducedMotion.matches ? "auto" : "smooth",
    });
  };

  const buildDot = (page) => {
    const dot = document.createElement("button");
    dot.type = "button";
    dot.className = "review-carousel-dot";
    dot.setAttribute("aria-label", dotList.dataset.reviewCarouselDots.replace("%{page}", page + 1));
    dot.addEventListener("click", () => goToPage(page));
    return dot;
  };

  const renderDots = () => {
    if (dots.length === pageCount()) return;

    dots = Array.from({ length: pageCount() }, (_, page) => buildDot(page));
    dotList.replaceChildren(...dots);
  };

  const syncCarousel = () => {
    renderDots();

    const page = currentPage();
    dots.forEach((dot, index) => dot.setAttribute("aria-current", String(index === page)));
    arrows.forEach((arrow) => { arrow.disabled = !withinRange(page + stepOf(arrow)); });
  };

  arrows.forEach((arrow) => {
    arrow.addEventListener("click", () => goToPage(currentPage() + stepOf(arrow)));
  });

  track.addEventListener("scroll", syncCarousel, { passive: true });
  window.addEventListener("resize", syncCarousel);

  syncCarousel();
  carousel.setAttribute("data-ready", "");
}

initializeRevealAnimations();
initializeMobileStickyCta();
initializeReviewCarousel();
initializeCtaTracking();
captureAttribution(window.location.search);
